// components/Login/Login.jsx
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { loginUser, clearError } from '../../redux/slices/authSlice';
import classes from './Login.module.css';

const Login = () => {
    const [formData, setFormData] = useState({
        email: '',
        password: ''
    });
    const [showPassword, setShowPassword] = useState(false);

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { loading, error, isLoggedIn } = useSelector((state) => state.auth);

    // Redirect if already logged in
    useEffect(() => {
        if (isLoggedIn) {
            const from = location.state?.from?.pathname || '/dashboard';
            navigate(from, { replace: true });
        }
    }, [isLoggedIn, navigate, location]);

    // Clear error when component mounts
    useEffect(() => {
        dispatch(clearError());
    }, [dispatch]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.email || !formData.password) return;

        dispatch(loginUser(formData));
    };

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    return (
        <div className={classes.loginPage}>
            <div className={classes.backgroundPattern}></div>

            <div className={classes.container}>
                <div className={classes.leftSection}>
                    <div className={classes.brandSection}>
                        <Link to="/" className={classes.logoLink}>
                            <h1 className={classes.logo}>Cent Capital</h1>
                        </Link>
                        <p className={classes.tagline}>Make sense of your cents</p>
                    </div>

                    <div className={classes.welcomeContent}>
                        <h2 className={classes.welcomeTitle}>Welcome back to your financial co-pilot</h2>
                        <p className={classes.welcomeSubtitle}>
                            Securely log in to continue your journey toward financial clarity and prosperity.
                        </p>
                    </div>
                </div>

                <div className={classes.rightSection}>
                    <div className={classes.formContainer}>
                        <div className={classes.formHeader}>
                            <h3 className={classes.formTitle}>Sign In</h3>
                            <p className={classes.formSubtitle}>Enter your credentials to access your account</p>
                        </div>

                        {error && (
                            <div className={classes.errorMessage}>
                                <span className={classes.errorIcon}>⚠</span>
                                {error}
                            </div>
                        )}

                        <form className={classes.form} onSubmit={handleSubmit}>
                            <div className={classes.inputGroup}>
                                <label htmlFor="email" className={classes.label}>Email Address</label>
                                <div className={classes.inputWrapper}>
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        className={classes.input}
                                        placeholder="Enter your email"
                                        required
                                    />
                                </div>
                            </div>

                            <div className={classes.inputGroup}>
                                <label htmlFor="password" className={classes.label}>Password</label>
                                <div className={classes.inputWrapper}>
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        id="password"
                                        name="password"
                                        value={formData.password}
                                        onChange={handleInputChange}
                                        className={classes.input}
                                        placeholder="Enter your password"
                                        required
                                    />
                                    <button
                                        type="button"
                                        className={classes.passwordToggle}
                                        onClick={togglePasswordVisibility}
                                    >
                                        {showPassword ? '👁' : '👁‍🗨'}
                                    </button>
                                </div>
                            </div>

                            <button
                                type="submit"
                                className={classes.submitButton}
                                disabled={loading || !formData.email || !formData.password}
                            >
                                {loading ? (
                                    <span className={classes.loadingContent}>
                                        <span className={classes.spinner}></span>
                                        Signing In...
                                    </span>
                                ) : (
                                    'Sign In'
                                )}
                            </button>
                        </form>

                        <div className={classes.divider}>
                            <span className={classes.dividerText}>or</span>
                        </div>

                        <button className={classes.googleButton}>
                            <svg className={classes.googleIcon} viewBox="0 0 24 24">
                                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                            </svg>
                            Continue with Google
                        </button>

                        <div className={classes.footer}>
                            <p className={classes.footerText}>
                                Don't have an account?{' '}
                                <Link to="/register" className={classes.footerLink}>
                                    Create one here
                                </Link>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;